// Thin fetch wrappers for the hq worker's /api/admin and /api/analytics
// surfaces. All calls send the session cookie (credentials: "include").

import type {
  AdminUser,
  AdminUserPage,
  AdminUserBilling,
  AdminSignupStats,
  AdminCreditResult,
  AdminUsageRow,
} from "@whtvrwrks/types";

export type {
  AdminUser,
  AdminUserPage,
  AdminUserBilling,
  AdminSignupStats,
  AdminCreditResult,
  AdminUsageRow,
};

// Mirror of the worker's analytics response shapes (apps/hq-worker/src/analytics.ts).
export type UsageResponse = {
  configured: boolean;
  days: number;
  rows: AdminUsageRow[];
  totals: {
    inputTokens: number;
    outputTokens: number;
    cachedInputTokens: number;
    costMcents: number;
    turns: number;
  };
  error?: string;
};

export type CloudflareResponse = {
  configured: boolean;
  days: number;
  workers: Array<{
    scriptName: string;
    requests: number;
    errors: number;
    subrequests: number;
  }>;
  error?: string;
};

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: "include" });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`);
  }
  return (await res.json()) as T;
}

export function listUsers(opts: {
  search?: string;
  cursor?: number | null;
  limit?: number;
}): Promise<AdminUserPage> {
  const p = new URLSearchParams();
  if (opts.search) p.set("search", opts.search);
  if (opts.cursor != null) p.set("cursor", String(opts.cursor));
  if (opts.limit) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return get<AdminUserPage>(`/api/admin/users${qs ? `?${qs}` : ""}`);
}

export function getUser(id: string): Promise<AdminUser> {
  return get<AdminUser>(`/api/admin/users/${encodeURIComponent(id)}`);
}

export function getUserBilling(id: string): Promise<AdminUserBilling> {
  return get<AdminUserBilling>(
    `/api/admin/users/${encodeURIComponent(id)}/billing`,
  );
}

export function creditUser(
  id: string,
  amountMcents: number,
  reason?: string,
): Promise<AdminCreditResult> {
  return post<AdminCreditResult>(
    `/api/admin/users/${encodeURIComponent(id)}/credit`,
    { amountMcents, reason },
  );
}

export function getSignupStats(days: number): Promise<AdminSignupStats> {
  return get<AdminSignupStats>(`/api/admin/signups?days=${days}`);
}

export function getUsage(days: number): Promise<UsageResponse> {
  return get<UsageResponse>(`/api/analytics/usage?days=${days}`);
}

export function getCloudflare(days: number): Promise<CloudflareResponse> {
  return get<CloudflareResponse>(`/api/analytics/cloudflare?days=${days}`);
}
